import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, CalendarClock, CheckCircle2, History, Loader2, Pencil, Play, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { EdytorHarmonogramu } from '@/components/EdytorHarmonogramu';
import { ApiError, czyStarszaInstancja } from '@/lib/api';
import {
  listaHarmonogramow,
  przebiegiHarmonogramu,
  uruchomHarmonogram,
  usunHarmonogram,
  type Harmonogram,
} from '@/lib/harmonogramy-api';
import { useI18n } from '@/lib/i18n';
import { useIslands } from '@/lib/islands';

/**
 * HARMONOGRAMY WYSYŁKI RAPORTÓW.
 *
 * ⚠️ EKRAN ODPOWIADA NA „CZY TO W OGÓLE CHODZI", a nie tylko „co jest zaplanowane". Harmonogram,
 * który przestał wychodzić, wygląda w spisie definicji identycznie jak taki, który chodzi bez
 * zarzutu — dlatego przy każdym wierszu stoi wynik OSTATNIEGO PRZEBIEGU, a nie sama pora następnego.
 *
 * ⚠️ WYSYŁKI JESZCZE NIE MA i ekran mówi to wprost. Przebieg składa plik i zapisuje ślad; bez tego
 * zdania pusta kolumna „odbiorcy" i brak maila w skrzynce czytałyby się jak awaria.
 */

const fmtKiedy = (iso: string | null): string =>
  iso === null ? '—' : new Date(iso).toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' });

const fmtRozmiar = (bajtow: number | null): string =>
  bajtow === null ? '—' : bajtow < 1024 * 1024 ? `${Math.round(bajtow / 1024)} kB` : `${(bajtow / 1048576).toFixed(1)} MB`;

export function Harmonogramy() {
  const { t } = useI18n();
  const { groupId } = useIslands();
  const toast = useToast();
  const qc = useQueryClient();

  const [edytowany, setEdytowany] = useState<Harmonogram | null>(null);
  const [nowy, setNowy] = useState(false);
  const [historia, setHistoria] = useState<Harmonogram | null>(null);
  const [doUsuniecia, setDoUsuniecia] = useState<Harmonogram | null>(null);

  const lista = useQuery({
    queryKey: ['harmonogramy', groupId],
    queryFn: () => listaHarmonogramow(groupId!),
    enabled: Boolean(groupId),
  });

  const odswiez = () => void qc.invalidateQueries({ queryKey: ['harmonogramy', groupId] });

  const uruchom = useMutation({
    mutationFn: (id: string) => uruchomHarmonogram(id),
    onSuccess: (p) => {
      if (p.status === 'OK') toast.success(t('harm.uruchomionoOk').replace('{n}', String(p.rows ?? 0)));
      else toast.error(`${t('harm.uruchomionoBlad')}: ${p.error ?? ''}`);
      odswiez();
    },
    onError: (e: Error) => toast.error(e instanceof ApiError ? e.message : t('common.error')),
  });

  const usun = useMutation({
    mutationFn: (id: string) => usunHarmonogram(id),
    onSuccess: () => {
      toast.success(t('harm.usunieto'));
      setDoUsuniecia(null);
      odswiez();
    },
    onError: (e: Error) => toast.error(e instanceof ApiError ? e.message : t('common.error')),
  });

  const limity = lista.data?.limity;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <CalendarClock className="size-6" />
            {t('harm.tytul')}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('harm.podtytul')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to="/raportowanie">
              <ArrowLeft className="size-4" />
              {t('harm.wrocDoRaportow')}
            </Link>
          </Button>
          <Button size="sm" disabled={!groupId} onClick={() => setNowy(true)}>
            <Plus className="size-4" />
            {t('harm.dodaj')}
          </Button>
        </div>
      </div>

      {/*
        ⚠️ TO ZDANIE ZOSTAJE DO CZASU, GDY WYSYŁKA RUSZY. Bez niego pusta skrzynka po zaplanowaniu
        raportu czyta się jak usterka, a nie jak funkcja, której jeszcze nie ma.
      */}
      <div className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
        {t('harm.bezWysylki')}
      </div>

      {lista.isPending && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}
      {lista.isError &&
        (czyStarszaInstancja(lista.error) ? (
          /* Front bywa wdrożony przed API — patrz `czyStarszaInstancja`. */
          <p className="rounded-md border border-border bg-muted/40 p-3 text-sm">{t('common.starszaInstancja')}</p>
        ) : (
          <p className="text-sm text-destructive">
            {t('common.error')}: {(lista.error as Error).message}
          </p>
        ))}
      {lista.data?.rows.length === 0 && <p className="text-sm text-muted-foreground">{t('harm.pusto')}</p>}

      <div className="grid gap-2">
        {(lista.data?.rows ?? []).map((h) => {
          const p = h.ostatniPrzebieg;
          /** Plik ponad granicę poczty nie odbije się u nas, tylko u operatora — ostrzegamy zawczasu. */
          const zaDuzy = p?.bytes != null && limity != null && p.bytes > limity.bajtowZalacznika;
          return (
            <div key={h.id} className="rounded-lg border border-border p-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{h.name}</span>
                    {!h.active && <Badge variant="neutral">{t('harm.wylaczony')}</Badge>}
                    <Badge variant="neutral">{t(`harm.raport.${h.report}`)}</Badge>
                    <Badge variant="neutral">{h.format.toUpperCase()}</Badge>
                    {/*
                      ⚠️ „bez nagłówków" WIDAĆ NA LIŚCIE, bo to jedyna różnica między dwoma plikami
                      o tej samej nazwie i tym samym formacie — a różnica przesądza, czy program
                      klienta plik przyjmie. Ukryta w oknie edycji byłaby nie do sprawdzenia bez
                      otwierania każdego harmonogramu po kolei.
                    */}
                    {h.format === 'txt' && !h.naglowkiTxt && (
                      <Badge variant="neutral">{t('harm.naglowkiTxtNie').toLowerCase()}</Badge>
                    )}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t(`harm.czestotliwosc.${h.frequency}`)
                      .replace('{godzina}', `${String(h.hour).padStart(2, '0')}:${String(h.minute).padStart(2, '0')}`)
                      .replace('{dzien}', h.weekday === null ? '' : t(`harm.dzien.${h.weekday}`))
                      .replace('{dzienMiesiaca}', String(h.dayOfMonth ?? ''))}
                    {h.period !== null && <> · {t(`harm.okres.${h.period}`)}</>}
                    {' · '}
                    {t('harm.nastepny')} <span className="font-medium">{fmtKiedy(h.nextRunAt)}</span>
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {h.recipients.length === 0 ? t('harm.brakOdbiorcow') : h.recipients.join(', ')}
                  </p>
                </div>

                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    title={t('harm.uruchomHint')}
                    disabled={uruchom.isPending}
                    onClick={() => uruchom.mutate(h.id)}
                  >
                    {uruchom.isPending && uruchom.variables === h.id ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Play className="size-4" />
                    )}
                    {t('harm.uruchom')}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setHistoria(h)} title={t('harm.historia')}>
                    <History className="size-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setEdytowany(h)} title={t('common.edit')}>
                    <Pencil className="size-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setDoUsuniecia(h)} title={t('common.delete')}>
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </div>

              {/*
                ⚠️ WYNIK OSTATNIEGO PRZEBIEGU W WIERSZU, nie po kliknięciu. Bez tego harmonogram, który
                od miesiąca kończy się błędem, wygląda dokładnie jak ten, który chodzi bez zarzutu.
              */}
              <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-border/60 pt-2 text-xs">
                {p === null ? (
                  <span className="text-muted-foreground">{t('harm.jeszczeNieChodzil')}</span>
                ) : p.status === 'OK' ? (
                  <>
                    <Badge variant="success">
                      <CheckCircle2 className="size-3" />
                      {t('harm.ostatniOk')}
                    </Badge>
                    <span className="text-muted-foreground">
                      {fmtKiedy(p.startedAt)} · {t('harm.wierszy').replace('{n}', String(p.rows ?? 0))} ·{' '}
                      {fmtRozmiar(p.bytes)}
                      {p.periodFrom !== null && (
                        <>
                          {' · '}
                          {p.periodFrom} → {p.periodTo}
                        </>
                      )}
                    </span>
                    {zaDuzy && (
                      <Badge variant="warning" title={t('harm.zaDuzyHint')}>
                        <AlertTriangle className="size-3" />
                        {t('harm.zaDuzy')}
                      </Badge>
                    )}
                  </>
                ) : (
                  <>
                    <Badge variant="destructive">
                      <AlertTriangle className="size-3" />
                      {t('harm.ostatniBlad')}
                    </Badge>
                    <span className="min-w-0 break-words text-muted-foreground">
                      {fmtKiedy(p.startedAt)} · {p.error ?? ''}
                    </span>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {(nowy || edytowany !== null) && groupId !== null && (
        <EdytorHarmonogramu
          groupId={groupId}
          harmonogram={edytowany}
          onClose={() => {
            setNowy(false);
            setEdytowany(null);
          }}
          onZapisano={() => {
            setNowy(false);
            setEdytowany(null);
            odswiez();
          }}
        />
      )}

      {historia !== null && <HistoriaPrzebiegow harmonogram={historia} onClose={() => setHistoria(null)} />}

      {doUsuniecia !== null && (
        <Dialog open onOpenChange={(v) => !v && setDoUsuniecia(null)}>
          <DialogContent title={t('harm.usunTytul')} description={doUsuniecia.name}>
            <p className="text-sm">{t('harm.usunPytanie')}</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDoUsuniecia(null)}>
                {t('common.cancel')}
              </Button>
              <Button variant="destructive" disabled={usun.isPending} onClick={() => usun.mutate(doUsuniecia.id)}>
                {usun.isPending && <Loader2 className="animate-spin" />}
                {t('common.delete')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

/**
 * HISTORIA PRZEBIEGÓW — także nieudanych.
 *
 * ⚠️ POKAZUJEMY WYLICZONY OKRES KAŻDEGO PRZEBIEGU. Definicja mówi „poprzedni tydzień", więc bez
 * tych dwóch dat nie da się po fakcie odpowiedzieć na pytanie „którego tygodnia dotyczyła wysyłka
 * z marca" — a to jest pierwsze pytanie przy sporze o rozliczenie.
 */
function HistoriaPrzebiegow({ harmonogram, onClose }: { harmonogram: Harmonogram; onClose: () => void }) {
  const { t } = useI18n();
  const przebiegi = useQuery({
    queryKey: ['harmonogram-przebiegi', harmonogram.id],
    queryFn: () => przebiegiHarmonogramu(harmonogram.id),
  });

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent title={t('harm.historia')} description={harmonogram.name} className="w-[min(46rem,calc(100vw-2rem))]">
        {przebiegi.isPending && <p className="text-xs text-muted-foreground">{t('common.loading')}</p>}
        {przebiegi.data?.rows.length === 0 && <p className="text-xs text-muted-foreground">{t('harm.jeszczeNieChodzil')}</p>}
        <div className="max-h-96 overflow-y-auto">
          {(przebiegi.data?.rows ?? []).map((p) => (
            <div key={p.id} className="border-b border-border/60 py-2 text-xs last:border-0">
              <p className="flex flex-wrap items-center gap-2">
                {p.status === 'OK' ? (
                  <Badge variant="success">{t('harm.ostatniOk')}</Badge>
                ) : (
                  <Badge variant="destructive">{t('harm.ostatniBlad')}</Badge>
                )}
                <span>{fmtKiedy(p.startedAt)}</span>
                {p.periodFrom !== null && (
                  <span className="text-muted-foreground">
                    {p.periodFrom} → {p.periodTo}
                  </span>
                )}
                {p.status === 'OK' && (
                  <span className="text-muted-foreground">
                    {t('harm.wierszy').replace('{n}', String(p.rows ?? 0))} · {fmtRozmiar(p.bytes)}
                  </span>
                )}
              </p>
              {p.error !== null && <p className="mt-0.5 break-words text-destructive">{p.error}</p>}
              {p.fileName !== null && <p className="mt-0.5 font-mono text-muted-foreground">{p.fileName}</p>}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
